/**
 * Tag constants for value type identification
 */
const mapTag = '[object Map]'
const setTag = '[object Set]'
const arrayTag = '[object Array]'
const argsTag = '[object Arguments]'

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
const objectProto = Object.prototype
const hasOwnProperty = objectProto.hasOwnProperty
const toString = objectProto.toString


/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 */
function isArrayLike(value: any): boolean {
  return value != null && typeof value !== 'function' && isLength(value.length)
}

/**
 * Checks if `value` is a valid array-like length.
 *
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 */
function isLength(value: any): boolean {
  return typeof value === 'number' && value > -1 && value % 1 === 0 && value <= Number.MAX_SAFE_INTEGER
}

/**
 * Checks if `value` is likely a prototype object.
 *
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */
function isPrototype(value: any): boolean {
  const Ctor = value && value.constructor
  const proto = (typeof Ctor === 'function' && Ctor.prototype) || objectProto
  return value === proto
}

/**
 * Checks if `value` is a buffer.
 *
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
 */
function isBuffer(value: any): boolean {
  return value != null && value.constructor != null &&
    typeof value.constructor.isBuffer === 'function' && value.constructor.isBuffer(value)
}

/**
 * Checks if `value` is classified as a typed array.
 *
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 */
function isTypedArray(value: any): boolean {
  const tag = getTag(value)
  return tag.startsWith('[object ') && tag.endsWith('Array]') &&
    tag !== arrayTag && tag !== argsTag
}

/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object, else `false`.
 */
function isArguments(value: any): boolean {
  return getTag(value) === argsTag ||
    (value != null && typeof value === 'object' && 'callee' in value && typeof value.length === 'number')
}

/**
 * Gets the `toStringTag` of `value`.
 *
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function getTag(value: any): string {
  if (value == null) {
    return value === undefined ? '[object Undefined]' : '[object Null]'
  }
  return toString.call(value)
}

/**
 * The base implementation of `keys` which doesn't treat sparse arrays as dense.
 *
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeys(object: object): string[] {
  if (object == null) {
    return []
  }
  return Object.keys(Object(object))
}

/**
 * Checks if `value` is an empty object, collection, map, or set.
 *
 * Objects are considered empty if they have no own enumerable string keyed
 * properties.
 *
 * Array-like values such as `arguments` objects, arrays, buffers, strings, or
 * jQuery-like collections are considered empty if they have a `length` of `0`.
 * Similarly, maps and sets are considered empty if they have a `size` of `0`.
 *
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is empty, else `false`.
 */
export function isEmpty(value: any): boolean {
  if (value == null) {
    return true
  }

  if (isArrayLike(value) &&
    (Array.isArray(value) || typeof value === 'string' || typeof value.splice === 'function' ||
      isBuffer(value) || isTypedArray(value) || isArguments(value))) {
    return !value.length
  }

  const tag = getTag(value)
  if (tag === mapTag || tag === setTag) {
    return !value.size
  }

  if (isPrototype(value)) {
    return !baseKeys(value).length
  }

  for (const key in value) {
    if (hasOwnProperty.call(value, key)) {
      return false
    }
  }
  return true
}